import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { SEO } from '@/components/seo'
import { BackgroundOrbs } from '@/components/layout'
import IntroAnimation, { type IntroVariant } from '@/components/IntroAnimation'
import GoogleAnalytics from '@/components/GoogleAnalytics'

interface GatewayCard {
  variant: IntroVariant
  title: string
  description: string
  bullets: string[]
  accentColor: string
  hoverBorder: string
  iconBg: string
  navigateTo: string
  icon: React.ReactNode
}

const cards: GatewayCard[] = [
  {
    variant: 'adviser',
    title: "I'm a Financial Adviser",
    description: 'AI-powered software built for advisers.',
    bullets: [
      'AI-powered suitability reports',
      'Meeting transcription & notes',
      'Practice management tools',
    ],
    accentColor: '#14b8a6',
    hoverBorder: 'hover:border-[rgba(20,184,166,0.4)]',
    iconBg: 'from-[rgba(20,184,166,0.2)] to-[rgba(20,184,166,0.1)]',
    navigateTo: '/advisers',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 7h-4a2 2 0 0 1-2-2V1" />
        <path d="M16 1H4a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7l-6-6z" />
        <path d="M8 13h8M8 17h5" />
      </svg>
    ),
  },
  {
    variant: 'dfm',
    title: "I'm an Investment Manager",
    description: 'Reduce admin and streamline operations.',
    bullets: [
      'IC paper & document generation',
      'Meeting capture & workflow',
      'Reduce operational admin',
    ],
    accentColor: '#f59e0b',
    hoverBorder: 'hover:border-[rgba(245,158,11,0.4)]',
    iconBg: 'from-[rgba(245,158,11,0.2)] to-[rgba(245,158,11,0.1)]',
    navigateTo: '/investment-managers',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 16l4-8 4 4 4-6" />
      </svg>
    ),
  },
  {
    variant: 'mortgage',
    title: "I'm a Mortgage Adviser",
    description: 'AI-powered software built for mortgage advisers.',
    bullets: [
      'AI-powered client documentation',
      'Meeting transcription & notes',
      'Practice management tools',
    ],
    accentColor: '#3b82f6',
    hoverBorder: 'hover:border-[rgba(59,130,246,0.4)]',
    iconBg: 'from-[rgba(59,130,246,0.2)] to-[rgba(59,130,246,0.1)]',
    navigateTo: '/mortgage-advisers',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
]

export default function GatewayPage() {
  const navigate = useNavigate()
  const [activeVariant, setActiveVariant] = useState<IntroVariant | null>(null)
  const [targetPath, setTargetPath] = useState<string | null>(null)

  const handleCardClick = useCallback((card: GatewayCard) => {
    setActiveVariant(card.variant)
    setTargetPath(card.navigateTo)
  }, [])

  const handleIntroComplete = useCallback(() => {
    if (targetPath) {
      navigate(targetPath)
    }
  }, [navigate, targetPath])

  return (
    <>
      <GoogleAnalytics />
      <SEO
        title="WealthSpace - AI-Powered Software for Financial Advisers, Investment Managers & Mortgage Advisers"
        description="WealthSpace reduces admin and improves outcomes for financial advisers, investment managers, and mortgage advisers. Choose your experience to learn more."
        keywords="WealthSpace, financial adviser software, investment manager software, DFM software, mortgage adviser software, AI suitability reports, meeting transcription"
        url="/"
      />

      {/* Intro Animation (shown after card click) */}
      {activeVariant && (
        <IntroAnimation variant={activeVariant} onComplete={handleIntroComplete} />
      )}

      <div className="min-h-screen w-full flex flex-col relative">
        <BackgroundOrbs />

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 relative z-[1]">
          {/* Heading */}
          <h1 className="font-serif text-[36px] sm:text-[clamp(42px,5vw,52px)] font-normal leading-[1.15] tracking-[-0.02em] text-brand-light text-center mb-3">
            Welcome to <span className="text-accent-tealLight">WealthSpace</span>
          </h1>
          <p className="text-lg text-brand-muted text-center mb-14">
            Choose your experience
          </p>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1200px] w-full">
            {cards.map((card) => (
              <button
                key={card.variant}
                onClick={() => handleCardClick(card)}
                className={`
                  group text-left p-8 rounded-2xl cursor-pointer
                  bg-gradient-to-br from-white/[0.03] to-white/[0.01]
                  border border-white/[0.06]
                  ${card.hoverBorder}
                  hover:-translate-y-1 hover:shadow-lg
                  transition-all duration-300
                `}
              >
                {/* Icon */}
                <div className={`w-14 h-14 rounded-[14px] bg-gradient-to-br ${card.iconBg} flex items-center justify-center mb-5`}>
                  {card.icon}
                </div>

                {/* Title */}
                <h2 className="font-serif text-[22px] text-brand-light mb-2">{card.title}</h2>
                <p className="text-sm text-brand-muted mb-5">{card.description}</p>

                {/* Bullets */}
                <ul className="space-y-2.5 mb-7">
                  {card.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center gap-2.5 text-sm text-brand-subtle">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={card.accentColor} strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold text-sm transition-all duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${card.accentColor}, ${card.accentColor}dd)`,
                    color: '#1a1f2e',
                  }}
                >
                  Enter
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Minimal Footer */}
        <div className="py-6 px-6 text-center relative z-[1]">
          <p className="text-[13px] text-brand-subtle">
            &copy; {new Date().getFullYear()} WealthSpace Ltd (Company No. 15396707). Registered in
            England &amp; Wales.
          </p>
        </div>
      </div>
    </>
  )
}
